import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Task } from 'graphql-common';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import AssetChip from '@lib/components/AssetChip/AssetChip';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import useTaskAccess from '@lib/hooks/useTaskAccess';
import styles from './TaskCompletionsModal.module.scss';
import { TaskCompletionsModalUrls } from './types';

type Props = {
  task: Pick<Task, 'id' | 'site' | 'siteArea' | 'asset'>;
  // Shown before the location chips, separated by a divider — e.g. the task
  // chip or the timeline period.
  leadingItem?: React.ReactNode;
  // A date leading item never shrinks/ellipsizes, unlike a task chip.
  isLeadingItemDate?: boolean;
  urls: TaskCompletionsModalUrls;
  className?: string;
};

/**
 * Task summary strip (leading item, Site / Site Area / Asset chips and a
 * "View task" link) shown above a task's completions — both in the
 * completions list and in the completions picker.
 */
export default function TaskCompletionsHeaderRow({
  task,
  leadingItem,
  isLeadingItemDate,
  urls,
  className,
}: Props) {
  const { t } = useTranslation();
  const { canOpenTask } = useTaskAccess();

  return (
    <div className={classNames(styles.headerRow, className)}>
      <div className={styles.headerLeftGroup}>
        {leadingItem && (
          <>
            <div
              className={classNames(styles.headerItem, {
                [styles.headerItemDate]: isLeadingItemDate,
              })}
            >
              {leadingItem}
            </div>
            <div className={styles.headerDivider} />
          </>
        )}
        <div className={styles.headerChips}>
          {task.site?.name && (
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.small}
              leftIcon="home_pin"
              leftIconSymbolsOutlined
              label={task.site.name}
            />
          )}
          {task.siteArea?.name && (
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.small}
              leftIcon="location_on"
              leftIconSymbolsOutlined
              label={task.siteArea.name}
            />
          )}
          {task.asset && (
            <AssetChip asset={task.asset} size={ChipSizes.small} />
          )}
        </div>
      </div>
      {canOpenTask && (
        <Button
          buttonText={t('view-task')}
          buttonType={ButtonTypes.primaryText}
          buttonSize={ButtonSizes.small}
          rightIcon="arrow_outward"
          to={urls.getTaskUrl(task.id)}
          target="_blank"
        />
      )}
    </div>
  );
}
